import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';

import { dynamicPrice, normalizeISO } from '@common/helpers/helperFunctions';
import Config from '@skin_config';
import DateCountdown from '@app/dev/terminal/components/dateCountdown';
import { TournamentStageEnum, TournamentStageViewEnum } from '@app/dev/constants/enums/tournament';
import { useDataFetch } from '@common/hooks/dataFetch';
import Loader from '@common/components/loader';
import { useTranslation } from '@common/hooks/translation';
import { swarmRequest } from '@common/helpers/util/requestMiddleware';
import TournamentPrizes from './prizes';
import TournamentLeaderboard from './leaderboard';
import TournamentRules from './rules';
import { restrictForLoggedIn } from '../helpers';

const TournamentDetails = ({ routeParams }) => {
  const t = useTranslation();
  const { id } = routeParams;

  const [refetchCounter, setRefetchCounter] = useState(0);
  const [data, , initialLoading] = useDataFetch(() => swarmRequest({ tournament_id: id }, 'get_tournament'), [refetchCounter]);

  const tournament = data?.result;

  useEffect(() => {
    const timeout = setTimeout(() => setRefetchCounter(refetchCounter + 1), 20000);
    return () => clearTimeout(timeout);
  }, [refetchCounter]);

  if (initialLoading || !tournament) return <Loader />;

  const bannerImage = tournament.DetailsBannerImages[0]?.Images[0]?.ImageUrl;

  const joinTournament = restrictForLoggedIn(async () => {
    const result = await swarmRequest({ tournament_id: tournament.Id }, 'join_to_tournament');
  });

  const statusClassName = classNames({
    'live-status': tournament.Stage === TournamentStageEnum.Live,
    'finished-status': tournament.Stage === TournamentStageEnum.Finished,
  });

  return (
    <div className="tournament-details">
      <div className="tournament-info-details" style={{ backgroundImage: `url(${bannerImage})` }}>
        <div className="tournament-info-content">
          <div className="tournament-info-top">
            <h2>{tournament.Name}</h2>
            <h3 className="prize-fund">{dynamicPrice(Config, tournament.PrizeFund, tournament.CurrencyId)}</h3>

            {tournament.Stage === TournamentStageEnum.Live
              ? <DateCountdown seconds={(moment(normalizeISO(tournament.EndDate)) - moment()) / 1000} />
              : <h3>{t(TournamentStageViewEnum[tournament.Stage])}</h3>}

            <p className="tournament-status">
              {t('Status')}
              :
              <span className={statusClassName}>{t(TournamentStageViewEnum[tournament.Stage])}</span>
            </p>

            <div className="tournament-actions">
              <button className="tournament-but join" disabled={!tournament.IsJoinable || tournament.JoinDate} onClick={joinTournament}>
                {tournament.JoinDate ? t('Joined') : t('Join')}
              </button>
            </div>
          </div>
          <div className="tournament-content-info">
            <div className="tournament-info-block">
              <h3>{t('Registration Date')}</h3>
              <div className="tournament-info-inner">
                <div className="tournament-info-inner-row">
                  <h5>{t('Start')}</h5>
                  <p>{moment(normalizeISO(tournament.RegistrationStartDate)).format(Config.main.dateTimeFormat)}</p>
                </div>
                <div className="tournament-info-inner-row">
                  <h5>{t('End')}</h5>
                  <p>{moment(normalizeISO(tournament.RegistrationEndDate)).format(Config.main.dateTimeFormat)}</p>
                </div>
              </div>
            </div>
            <div className="tournament-info-block">
              <h3>{t('Tournament Date')}</h3>
              <div className="tournament-info-inner">
                <div className="tournament-info-inner-row">
                  <h5>{t('Start')}</h5>
                  <p>{moment(normalizeISO(tournament.StartDate)).format(Config.main.dateTimeFormat)}</p>
                </div>
                <div className="tournament-info-inner-row">
                  <h5>{t('End')}</h5>
                  <p>{moment(normalizeISO(tournament.EndDate)).format(Config.main.dateTimeFormat)}</p>
                </div>
              </div>
            </div>
            <div className="tournament-info-block">
              <h3>{t('Bet Range')}</h3>
              <div className="tournament-info-inner">
                <div className="tournament-info-inner-row">
                  <h5>{t('Minimum Bet')}</h5>
                  <p className="bet">{dynamicPrice(Config, tournament.MinBet, tournament.CurrencyId)}</p>
                </div>
                <div className="tournament-info-inner-row">
                  <h5>{t('Maximum Bet')}</h5>
                  <p className="bet">{tournament.MaxBet ? dynamicPrice(Config, tournament.MaxBet) : '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="tournament-stats-wrap">
        <div className="tournament-stats-wrap-inner">
          <TournamentPrizes tournament={tournament} />
          <TournamentLeaderboard tournament={tournament} />
          <TournamentRules tournament={tournament} />
        </div>
      </div>
    </div>
  );
};

export default TournamentDetails;
